import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { getPurchases, createPurchase, updatePurchase, deletePurchase, getUsers, getItems, Purchase, User, Item } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';

const PurchasesPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
  const [formData, setFormData] = useState<Partial<Purchase>>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [purchasesRes, usersRes, itemsRes] = await Promise.all([
        getPurchases(1, 100),
        getUsers(1, 100),
        getItems(1, 100),
      ]);

      setPurchases(purchasesRes.data.data || []);
      setUsers(usersRes.data.data || []);
      setItems(itemsRes.data.data || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load purchases data';
      setError(errorMessage);
      console.error('Purchases error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPurchase = () => {
    setEditingPurchase(null);
    setFormData({
      purchased_on: new Date().toISOString().split('T')[0],
    });
    setOpenDialog(true);
  };

  const handleEditPurchase = (purchase: Purchase) => {
    setEditingPurchase(purchase);
    // Format the dates for the HTML date input (YYYY-MM-DD)
    const formattedPurchase = {
      ...purchase,
      purchased_on: purchase.purchased_on ? new Date(purchase.purchased_on).toISOString().split('T')[0] : '',
      settled_on: purchase.settled_on ? new Date(purchase.settled_on).toISOString().split('T')[0] : ''
    };
    setFormData(formattedPurchase);
    setOpenDialog(true);
  };

  const handleDeletePurchase = async (userEmail: string, itemId: number) => {
    if (window.confirm('Are you sure you want to delete this purchase record?')) {
      try {
        // Find purchase by user_email and item_id since we might not have id
        const purchase = purchases.find(p => p.user_email === userEmail && p.item_id === itemId);
        if (purchase && purchase.id) {
          await deletePurchase(purchase.id);
          fetchData();
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to delete purchase record';
        setError(errorMessage);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingPurchase) {
        await updatePurchase(editingPurchase.id!, formData);
      } else {
        await createPurchase(formData as Purchase);
      }
      setOpenDialog(false);
      fetchData();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save purchase record';
      setError(errorMessage);
    }
  };



  const columns: GridColDef[] = isMobile ? [
    { 
      field: 'customer', 
      headerName: 'Customer', 
      width: 150,
      valueGetter: (params: any) => {
        if (!params || !params.row) return '';
        return `${params.row.fname} ${params.row.lname}`;
      },
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        );
      }
    },
    { 
      field: 'item', 
      headerName: 'Item', 
      width: 150,
      valueGetter: (params: any) => {
        if (!params || !params.row) return '';
        return params.row.display_name || params.row.name || `Item ${params.row.item_id}`;
      },
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        );
      }
    },
    { 
      field: 'price_paid', 
      headerName: 'Amount', 
      width: 100,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      }
    },
    { 
      field: 'purchased_on', 
      headerName: 'Date', 
      width: 120,
      valueFormatter: (dateValue: any) => {
        if (dateValue == null || dateValue === undefined || dateValue === '') {
          return 'No date';
        }
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) {
            return 'Invalid date';
          }
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        } catch (error) {
          return 'Error';
        }
      }
    },
    { 
      field: 'settled_on', 
      headerName: 'Settled', 
      width: 100,
      valueFormatter: (dateValue: any) => {
        if (dateValue == null || dateValue === undefined || dateValue === '') {
          return 'No';
        }
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) {
            return 'Invalid';
          }
          return 'Yes';
        } catch (error) {
          return 'Error';
        }
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditPurchase(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeletePurchase(params.row.user_email, params.row.item_id)}
          />,
        ];
      },
    },
  ] : [
    { 
      field: 'user_email', 
      headerName: 'Customer', 
      width: 250,
      renderCell: (params) => {
        const customerName = params.row.fname && params.row.lname ? 
          `${params.row.lname}, ${params.row.fname} (${params.row.user_email})` : 
          params.row.user_email || '';
        return (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={customerName}
          >
            {customerName}
          </Box>
        );
      }
    },
    { 
      field: 'item_id', 
      headerName: 'Item', 
      width: 250,
      renderCell: (params) => {
        const itemName = params.row.display_name || (params.row.code && params.row.name ? 
          `${params.row.code} - ${params.row.name}` : 
          `Item ${params.row.item_id}`);
        return (
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={itemName}
          >
            {itemName}
          </Box>
        );
      }
    },
    { 
      field: 'price_asked', 
      headerName: 'Price Asked (€)', 
      width: 150,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      }
    },
    { 
      field: 'price_paid', 
      headerName: 'Price Paid (€)', 
      width: 150,
      valueFormatter: (value: any) => {
        if (value == null || value === undefined) return '€0.00';
        return `€${Number(value).toFixed(2)}`;
      }
    },
    { 
      field: 'purchased_on', 
      headerName: 'Purchased On', 
      width: 180,
      valueFormatter: (dateValue: any) => {
        if (dateValue == null || dateValue === undefined || dateValue === '') {
          return 'No date';
        }
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) {
            return 'Invalid date';
          }
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (error) {
          return 'Error';
        }
      }
    },
    { 
      field: 'settled_on', 
      headerName: 'Settled On', 
      width: 150,
      valueFormatter: (dateValue: any) => {
        if (dateValue == null || dateValue === undefined || dateValue === '') {
          return 'Not settled';
        }
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) {
            return 'Invalid date';
          }
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (error) {
          return 'Error';
        }
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditPurchase(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeletePurchase(params.row.user_email, params.row.item_id)}
          />,
        ];
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Purchases Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPurchase}
        >
          Add Purchase
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={() => setError('')}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={purchases}
          columns={columns}
          loading={loading}
          getRowId={(row) => `${row.user_email}-${row.item_id}`}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          columnVisibilityModel={isMobile ? {
            user_email: false,
            item_id: false,
            price_asked: false,
            customer: true,
            item: true,
            price_paid: true,
            purchased_on: true,
            settled_on: true,
            actions: true
          } : {
            user_email: true,
            item_id: true,
            price_asked: true,
            customer: false,
            item: false,
            price_paid: true,
            purchased_on: true,
            settled_on: true,
            actions: true
          }}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPurchase ? 'Edit Purchase' : 'Add New Purchase'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={formData.user_email || ''}
                onChange={(e) => setFormData({ ...formData, user_email: e.target.value as string })}
                label="Customer"
                required
              >
                {users.map((user) => (
                  <MenuItem key={user.email} value={user.email}>
                    {user.fname} {user.lname} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Item</InputLabel>
              <Select
                value={formData.item_id || ''}
                onChange={(e) => setFormData({ ...formData, item_id: e.target.value as number })}
                label="Item"
                required
              >
                {items.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.code} - {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Price Asked (€)"
              type="number"
              value={formData.price_asked || ''}
              onChange={(e) => setFormData({ ...formData, price_asked: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              label="Price Paid (€)"
              type="number"
              value={formData.price_paid || ''}
              onChange={(e) => setFormData({ ...formData, price_paid: parseFloat(e.target.value) || 0 })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
            />
            <TextField
              label="Purchase Date"
              type="date"
              value={formData.purchased_on || ''}
              onChange={(e) => setFormData({ ...formData, purchased_on: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Settled On"
              type="date"
              value={formData.settled_on || ''}
              onChange={(e) => setFormData({ ...formData, settled_on: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty if not settled yet"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPurchase ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchasesPage;
